/** A preview card that appears when a name is hovered or focused (WCAG 1.4.13: dismissible,
 * hoverable, persistent).
 *
 * **It never carries the only copy of anything.** The card previews what the link points at; the
 * link itself is the way in and works without the card. Escape dismisses it, the pointer can move
 * onto it without it closing, and it opens on keyboard focus as well as on hover. It is portalled to
 * `document.body` and positioned in `fixed` coordinates because a table's scroll container would
 * clip an absolutely positioned child.
 *
 * `PreviewCard` is the surface alone; `DataTable`'s row preview reuses it.
 */

import {
  cloneElement,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import type { KeyboardEvent, ReactElement, ReactNode, RefObject } from "react";
import { createPortal } from "react-dom";

import styles from "./HoverCard.module.css";

const MARGIN = 8;
const OFFSET = 6;

/** The floating surface, placed under `anchor` (a viewport rectangle) and kept on screen. */
export function PreviewCard({
  anchor,
  id,
  children,
  onPointerEnter,
  onPointerLeave,
  testId = "preview-card",
  role = "tooltip",
  ariaLabel,
  className,
  align = "start",
  onKeyDown,
  popupRef,
}: {
  anchor: DOMRect;
  id: string;
  children: ReactNode;
  onPointerEnter?: () => void;
  onPointerLeave?: () => void;
  testId?: string;
  /** `tooltip` for a preview; `menu` / `listbox` when the surface is a popup the caller manages. */
  role?: "tooltip" | "menu" | "dialog";
  ariaLabel?: string;
  /** Replaces the card padding, for a surface that lays out its own rows. */
  className?: string;
  /** Which edge of the anchor the surface lines up with. */
  align?: "start" | "end";
  onKeyDown?: (event: KeyboardEvent<HTMLDivElement>) => void;
  popupRef?: RefObject<HTMLDivElement | null>;
}) {
  const own = useRef<HTMLDivElement>(null);
  const ref = popupRef ?? own;
  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return;
    const { width, height } = node.getBoundingClientRect();
    const wanted = align === "end" ? anchor.right - width : anchor.left;
    const left = Math.max(MARGIN, Math.min(wanted, window.innerWidth - width - MARGIN));
    const below = anchor.bottom + OFFSET;
    const top =
      below + height > window.innerHeight - MARGIN && anchor.top - OFFSET - height > MARGIN
        ? anchor.top - OFFSET - height
        : below;
    node.style.left = `${left}px`;
    node.style.top = `${top}px`;
    node.style.visibility = "visible";
  }, [anchor, align, ref]);
  return createPortal(
    <div
      ref={ref}
      id={id}
      role={role}
      aria-label={ariaLabel}
      onKeyDown={onKeyDown}
      className={`${styles.card} ${className ?? ""}`}
      data-testid={testId}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
    >
      {children}
    </div>,
    document.body,
  );
}

export function HoverCard({
  card,
  children,
  openDelay = 350,
  closeDelay = 120,
}: {
  /** What to show. Plain content: a tooltip may not hold controls. */
  card: ReactNode;
  /** The trigger, usually an `EntityLink`. It receives `aria-describedby` while the card is open. */
  children: ReactElement<{ "aria-describedby"?: string }>;
  openDelay?: number;
  closeDelay?: number;
}) {
  const id = useId();
  const wrap = useRef<HTMLSpanElement>(null);
  const timer = useRef<number | undefined>(undefined);
  const [anchor, setAnchor] = useState<DOMRect | null>(null);

  const clear = useCallback(() => window.clearTimeout(timer.current), []);
  const open = useCallback(
    (delay: number) => {
      clear();
      timer.current = window.setTimeout(() => {
        const rect = wrap.current?.getBoundingClientRect();
        if (rect) setAnchor(rect);
      }, delay);
    },
    [clear],
  );
  const close = useCallback(
    (delay: number) => {
      clear();
      timer.current = window.setTimeout(() => setAnchor(null), delay);
    },
    [clear],
  );

  useEffect(() => clear, [clear]);

  // A scroll or resize moves the trigger from under a fixed card: close rather than chase it.
  useEffect(() => {
    if (!anchor) return;
    const dismiss = () => setAnchor(null);
    window.addEventListener("scroll", dismiss, true);
    window.addEventListener("resize", dismiss);
    return () => {
      window.removeEventListener("scroll", dismiss, true);
      window.removeEventListener("resize", dismiss);
    };
  }, [anchor]);

  return (
    <span
      ref={wrap}
      className={styles.wrap}
      onPointerEnter={() => open(openDelay)}
      onPointerLeave={() => close(closeDelay)}
      onFocus={() => open(0)}
      onBlur={() => close(0)}
      onKeyDown={(event) => {
        if (event.key === "Escape" && anchor) {
          clear();
          setAnchor(null);
        }
      }}
    >
      {cloneElement(children, anchor ? { "aria-describedby": id } : {})}
      {anchor ? (
        <PreviewCard
          anchor={anchor}
          id={id}
          onPointerEnter={clear}
          onPointerLeave={() => close(closeDelay)}
          testId="hover-card"
        >
          {card}
        </PreviewCard>
      ) : null}
    </span>
  );
}
